import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Html5Qrcode } from 'html5-qrcode';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { GateService } from '../../api/services/gateService';
import { GateScanResponse } from '../../types/api/response/gateresponse';

const QR_READER_ELEMENT_ID = 'gate-qr-reader';

type InputMode = 'camera' | 'manual';

export const GateCheckInPage = () => {
  const dispatch = useDispatch();

  const [mode, setMode] = useState<InputMode>('camera');
  const [cameraLoading, setCameraLoading] = useState(false);
  const [restartKey, setRestartKey] = useState(0);

  const [gateToken, setGateToken] = useState('');
  const [scanning, setScanning] = useState(false);
  const [admitting, setAdmitting] = useState(false);
  const [scanResult, setScanResult] = useState<GateScanResponse | null>(null);
  const [admitted, setAdmitted] = useState(false);

  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);

  const stopCamera = useCallback(async () => {
    const instance = html5QrCodeRef.current;
    if (instance && instance.isScanning) {
      try {
        await instance.stop();
      } catch {
        // already stopped/stopping — nothing to do
      }
    }
  }, []);

  const handleScan = useCallback(
    async (tokenOverride?: string) => {
      const token = (tokenOverride ?? gateToken).trim();
      if (!token) {
        dispatch(showNotification({ message: 'Enter a gate token to scan.', severity: 'error' }));
        return;
      }

      setScanning(true);
      setScanResult(null);
      setAdmitted(false);
      try {
        const result = await GateService.scan(token);
        setGateToken(token);
        setScanResult(result);
      } catch (e: any) {
        dispatch(
          showNotification({
            message: e?.response?.data?.message ?? 'Failed to look up gate token.',
            severity: 'error',
          }),
        );
      } finally {
        setScanning(false);
      }
    },
    [dispatch, gateToken],
  );

  // Starts/stops the camera whenever `mode` switches to/from 'camera', and re-runs
  // (restarting the camera) whenever `restartKey` bumps — see handleReset below.
  useEffect(() => {
    if (mode !== 'camera') {
      stopCamera();
      return;
    }

    let cancelled = false;
    setCameraLoading(true);

    const startCamera = async () => {
      try {
        const cameras = await Html5Qrcode.getCameras();
        if (cancelled) return;
        if (!cameras || cameras.length === 0) {
          throw new Error('No camera found');
        }

        const instance = new Html5Qrcode(QR_READER_ELEMENT_ID);
        html5QrCodeRef.current = instance;

        await instance.start(
          // html5-qrcode only recognizes a plain facingMode string or { exact: ... } here —
          // NOT the native MediaTrackConstraints { ideal: ... } shape. A bare string is treated
          // as a soft preference by the browser (won't throw if no rear camera exists), so this
          // still falls back gracefully to whatever camera is available on a laptop.
          { facingMode: 'environment' },
          {
            fps: 10,
            // A fixed pixel box makes size/placement/distance finicky — anything outside
            // that exact box is invisible to the scanner. Scale it to the actual rendered
            // preview instead so it's forgiving of holding the QR further back or off-center.
            qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
              const edge = Math.floor(Math.min(viewfinderWidth, viewfinderHeight) * 0.8);
              return { width: edge, height: edge };
            },
          },
          async (decodedText) => {
            await stopCamera();
            handleScan(decodedText);
          },
          () => {
            // Fires continuously while no QR code is in frame — not an error, ignore.
          },
        );
      } catch (e: any) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('Gate Check-in camera init failed:', e?.name, e?.message, e);
        dispatch(
          showNotification({
            message: e?.name
              ? `${e.name}: ${e.message ?? 'Camera not available'} — enter the code manually.`
              : (e?.message ?? 'Camera not available — enter the code manually.'),
            severity: 'error',
          }),
        );
        setMode('manual');
      } finally {
        if (!cancelled) setCameraLoading(false);
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, restartKey]);

  const handleAdmit = async () => {
    setAdmitting(true);
    try {
      const result = await GateService.admit(gateToken.trim());
      setScanResult((prev) => (prev ? { ...prev, order_status: result.order_status } : prev));
      setAdmitted(true);
      dispatch(showNotification({ message: 'Customer admitted.', severity: 'success' }));
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to admit customer.',
          severity: 'error',
        }),
      );
    } finally {
      setAdmitting(false);
    }
  };

  const handleReset = () => {
    setGateToken('');
    setScanResult(null);
    setAdmitted(false);
    if (mode === 'camera') {
      setRestartKey((k) => k + 1);
    }
  };

  return (
    <Box
      sx={{ maxWidth: 480, mx: 'auto', mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Typography variant="h5">Gate Check-in</Typography>

      {!scanResult &&
        (mode === 'camera' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: 460,
                mx: 'auto',
                minHeight: cameraLoading ? 200 : undefined,
              }}
            >
              {/* Spinner is overlaid, not a flex sibling: html5-qrcode fixes the video width from
                  the reader's width at start, so the reader must already be full width. */}
              <div id={QR_READER_ELEMENT_ID} style={{ width: '100%' }} />
              {cameraLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
            </Box>
            <Button size="small" onClick={() => setMode('manual')} disabled={scanning || admitting}>
              Enter code manually instead
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Gate Token"
                fullWidth
                value={gateToken}
                onChange={(e) => setGateToken(e.target.value)}
                disabled={scanning || admitting}
              />
              <Button
                variant="contained"
                onClick={() => handleScan()}
                disabled={scanning || admitting}
              >
                {scanning ? <CircularProgress size={24} /> : 'Scan'}
              </Button>
            </Box>
            <Button size="small" onClick={() => setMode('camera')} disabled={scanning || admitting}>
              Use camera instead
            </Button>
          </Box>
        ))}

      {scanResult && (
        <Card variant="outlined">
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography>
              <strong>Order:</strong> {scanResult.order_no}
            </Typography>
            <Typography>
              <strong>Customer:</strong> {scanResult.customer_name}
            </Typography>
            <Typography>
              <strong>Items:</strong> {scanResult.item_count}
            </Typography>
            <Typography>
              <strong>Status:</strong> {scanResult.order_status}
            </Typography>
            <Typography>
              <strong>Bay:</strong> {scanResult.bay_number ?? 'Not assigned yet'}
            </Typography>

            <Divider sx={{ my: 1 }} />

            {admitted ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
                <CheckCircleIcon />
                <Typography fontWeight="bold">Admitted — allow entry</Typography>
              </Box>
            ) : (
              <Button
                variant="contained"
                color="success"
                onClick={handleAdmit}
                disabled={admitting}
              >
                {admitting ? <CircularProgress size={24} /> : 'Admit'}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {scanResult && (
        <Button onClick={handleReset} disabled={scanning || admitting}>
          Scan another
        </Button>
      )}
    </Box>
  );
};
